import { z } from 'zod'
import { CopilotSettings } from './live.ts'
import { ContainerType, queryBool, Task, TaskStatus } from './m1.ts'
import { patchOf } from './patch.ts'

/** M2 client + ingest API (§14, §15, §19.1/19.4, §12). Paths below /api/v1 unless noted. */

const id = z.uuid()
const isoDate = z.iso.date()
const isoDateTime = z.iso.datetime({ offset: true })

// ── Tasks (complete, §15) ─────────────────────────────────────────────────────────────────────
export const Energy = z.enum(['deep', 'shallow', 'any'])

export const TaskFull = Task.extend({
  energy: Energy,
  estimateMin: z.number().int().nullable(),
  assigneeId: id.nullable(),
  waitingOnPersonId: id.nullable(),
  agentReady: z.boolean(),
  agentSpec: z.string().nullable(),
  acceptanceCriteria: z.string().nullable(),
  recurrenceRule: z.string().nullable(),
  parentTaskId: id.nullable(),
  sourceType: z.string().nullable(),
  sourceRef: z.string().nullable(),
  sourceAnchor: z.string().nullable(),
  sortOrder: z.number().nullable(),
  /** §15.2 priority score (higher = more important) */
  score: z.number(),
  subtaskCount: z.number().int(),
  subtasksDone: z.number().int(),
  blockedByCount: z.number().int(),
  container: z.object({ id, title: z.string(), type: ContainerType }).nullable(),
})
export type TaskFull = z.infer<typeof TaskFull>

export const TaskDetail = TaskFull.extend({
  subtasks: z.array(TaskFull),
  blockedBy: z.array(z.object({ id, title: z.string(), status: TaskStatus })),
  blocking: z.array(z.object({ id, title: z.string(), status: TaskStatus })),
  /** where the task came from: note, meeting (with transcript anchor), notion, capture */
  source: z
    .object({
      type: z.string(),
      id: z.string(),
      title: z.string().nullable(),
      url: z.string().nullable(),
      anchorMs: z.number().nullable(),
    })
    .nullable(),
  waitingOn: z.object({ id, name: z.string() }).nullable(),
})
export type TaskDetail = z.infer<typeof TaskDetail>

export const ListTasksQueryV2 = z.object({
  containerId: id.optional(),
  inbox: queryBool.optional(),
  status: z.union([TaskStatus, z.literal('open')]).optional(),
  /** waiting view: status=waiting + nothing else */
  view: z.enum(['all', 'waiting', 'today', 'upcoming']).default('all'),
  /** do_date range (week view) */
  from: isoDate.optional(),
  to: isoDate.optional(),
  parentTaskId: id.optional(),
  /** top-level only (default true) – subtasks appear under their parent */
  topLevel: queryBool.default(true),
  sort: z.enum(['score', 'due', 'priority', 'manual', 'created']).default('score'),
  includeDone: queryBool.default(false),
  limit: z.coerce.number().int().min(1).max(500).default(300),
})

const TaskFields = {
  title: z.string().trim().min(1).max(500),
  containerId: id.nullable(),
  descriptionMd: z.string().max(100_000),
  status: TaskStatus,
  priority: z.number().int().min(1).max(4),
  dueDate: isoDate.nullable(),
  doDate: isoDate.nullable(),
  estimateMin: z.number().int().min(0).max(100_000).nullable(),
  energy: Energy,
  waitingOnPersonId: id.nullable(),
  agentReady: z.boolean(),
  agentSpec: z.string().max(50_000).nullable(),
  acceptanceCriteria: z.string().max(20_000).nullable(),
  recurrenceRule: z.string().max(500).nullable(),
  parentTaskId: id.nullable(),
  sortOrder: z.number().nullable(),
}
export const CreateTaskV2 = z
  .object({
    id: id.optional(),
    ...TaskFields,
    sourceType: z.string().max(50).nullable(),
    sourceRef: z.string().max(200).nullable(),
    sourceAnchor: z.string().max(200).nullable(),
  })
  .partial()
  .required({ title: true })
export type CreateTaskV2 = z.infer<typeof CreateTaskV2>
export const UpdateTaskV2 = z.object(TaskFields).partial()
export type UpdateTaskV2 = z.infer<typeof UpdateTaskV2>

/** POST /tasks/quick – the server parses (`morgen p1 #kunde-a ~30m @max`, §15.1) and resolves #slug/@person */
export const QuickAddTask = z.object({
  id: id.optional(),
  text: z.string().trim().min(1).max(1000),
  containerId: id.nullable().optional(),
  parentTaskId: id.optional(),
  sourceType: z.string().max(50).optional(),
  sourceRef: z.string().max(200).optional(),
})
export type QuickAddTask = z.infer<typeof QuickAddTask>

// PUT/DELETE /tasks/:id/blocked-by/:otherId → dependency "id is blocked by otherId" (links kind 'blocks')

// ── People (§19.6 basis) ──────────────────────────────────────────────────────────────────────
export const Person = z.object({
  id,
  name: z.string(),
  emails: z.array(z.string()),
  company: z.string().nullable(),
  role: z.string().nullable(),
  phone: z.string().nullable(),
  lastContactAt: isoDateTime.nullable(),
  containers: z.array(z.object({ id, title: z.string(), relation: z.string() })),
  openCommitments: z.object({ iOwe: z.number().int(), owedToMe: z.number().int() }),
  /** M4 light CRM (§19.6): other spellings of the name (attendees without e-mail) */
  aliases: z.array(z.string()).default([]),
  contactIntervalDays: z.number().int().nullable().default(null),
  followUpOn: isoDate.nullable().default(null),
  /** last contact is older than the person's (or the default) interval, or the follow-up date has come */
  contactDue: z.boolean().default(false),
})
export type Person = z.infer<typeof Person>
export const PersonCard = Person.extend({
  commitments: z.array(z.lazy(() => Commitment)),
  meetings: z.array(z.object({ id, title: z.string(), startedAt: isoDateTime })),
  notes: z.array(z.object({ id, title: z.string(), updatedAt: isoDateTime })),
  /** open tasks from meetings with this person or tied to their commitments */
  tasks: z
    .array(
      z.object({ id, title: z.string(), dueDate: isoDate.nullable(), containerTitle: z.string().nullable() }),
    )
    .default([]),
})
export const UpsertPerson = z.object({
  name: z.string().trim().min(1).max(200),
  emails: z.array(z.email()).max(20).default([]),
  company: z.string().max(200).nullable().optional(),
  role: z.string().max(200).nullable().optional(),
  phone: z.string().max(100).nullable().optional(),
  containerIds: z.array(id).optional(),
  aliases: z.array(z.string().trim().min(1).max(200)).max(30).optional(),
  contactIntervalDays: z.number().int().min(1).max(3650).nullable().optional(),
  followUpOn: isoDate.nullable().optional(),
})
export const UpdatePerson = patchOf(UpsertPerson)

// ── Meetings (§14) ────────────────────────────────────────────────────────────────────────────
export const TranscriptSegment = z.object({
  speaker: z.string(),
  start_ms: z.number().int().min(0),
  end_ms: z.number().int().min(0),
  text: z.string(),
  /** stable speaker key for renaming (live: 'me' | 'them' | 'them:<n>'; others: 'label:<original label>') */
  speaker_key: z.string().optional(),
  /** language reported by the transcription, e.g. 'de', 'en' */
  language: z.string().optional(),
})
export const MeetingSummary = z.object({
  id,
  title: z.string(),
  startedAt: isoDateTime,
  endedAt: isoDateTime.nullable(),
  containerId: id.nullable(),
  containerTitle: z.string().nullable(),
  /** live = recording right now (live co-pilot, ADR 0006) */
  status: z.enum(['live', 'processing', 'processed', 'failed']),
  source: z.enum(['ingest', 'native', 'live', 'import']),
  participants: z.array(z.object({ name: z.string(), email: z.string().nullable() })),
  noteId: id.nullable(),
  pendingSuggestions: z.number().int(),
})
export type MeetingSummary = z.infer<typeof MeetingSummary>
/** Renamed speakers: key (see TranscriptSegment.speaker_key) → display name, optionally linked to a person. */
export const SpeakerNames = z.record(
  z.string().max(200),
  z.object({ name: z.string().min(1).max(200), personId: id.nullable().optional() }),
)
export type SpeakerNames = z.infer<typeof SpeakerNames>

export const MeetingDetail = MeetingSummary.extend({
  summaryMd: z.string().nullable(),
  error: z.string().nullable(),
  hasTranscript: z.boolean(),
  /** language of the call ('de' | 'en' …); summary and suggestions are written in it */
  language: z.string(),
  /** plain notes from before the meeting notes editor (and the live mirror for the co-pilot) */
  userNotes: z.string().nullable(),
  /** the user's meeting notes as a regular note in the collaborative editor (ADR 0015); null until first used */
  notesNoteId: id.nullable(),
  speakerNames: SpeakerNames,
  suggestions: z.array(z.lazy(() => Suggestion)),
})
// GET /meetings/:id/transcript?aroundMs=&windowMs=30000 → { segments: TranscriptSegment[] }

// POST /meetings/:id/notes → { noteId } (creates the notes note on first use, ADR 0015)

/**
 * GET /meetings/:id/screenshots → MeetingScreenshot[]: images of the meeting's notes and summary note with their
 * AI reading (ADR 0015). POST /meetings/:id/screenshots/:attachmentId/retry → 204 (reads the image again).
 * status 'none' = not read (e.g. uploaded before the feature or an unsupported format).
 */
export const MeetingScreenshot = z.object({
  attachmentId: id,
  filename: z.string(),
  mime: z.string(),
  url: z.string(),
  createdAt: isoDateTime,
  status: z.enum(['none', 'pending', 'running', 'done', 'skipped', 'failed']),
  description: z.string().nullable(),
  text: z.string().nullable(),
  error: z.string().nullable(),
})
export type MeetingScreenshot = z.infer<typeof MeetingScreenshot>

// PATCH /meetings/:id → MeetingDetail
export const UpdateMeeting = z.object({ userNotes: z.string().max(200_000).nullable().optional() })
export type UpdateMeeting = z.infer<typeof UpdateMeeting>

/**
 * PATCH /meetings/:id/speakers → MeetingDetail. Merges into speaker_names (null removes a name), rewrites the
 * stored transcript's speaker labels, updates participants and people.last_contact_at for linked persons.
 * POST /meetings/:id/reprocess → MeetingDetail (status processing; meeting.process runs again, updates the
 * existing meeting note and only adds new suggestions).
 */
export const UpdateSpeakers = z.object({
  names: z.record(
    z.string().max(200),
    z.object({ name: z.string().trim().min(1).max(200), personId: id.nullable().optional() }).nullable(),
  ),
})
export type UpdateSpeakers = z.infer<typeof UpdateSpeakers>

// ── Decisions (§19.4) ─────────────────────────────────────────────────────────────────────────
export const DecisionStatus = z.enum(['proposed', 'accepted', 'superseded', 'rejected'])
export const Decision = z.object({
  id,
  containerId: id.nullable(),
  containerTitle: z.string().nullable(),
  noteId: id.nullable(),
  title: z.string(),
  status: DecisionStatus,
  decidedAt: isoDateTime.nullable(),
  supersedesId: id.nullable(),
  supersededById: id.nullable(),
  rationaleMd: z.string(),
  sourceType: z.string().nullable(),
  sourceRef: z.string().nullable(),
  sourceAnchor: z.string().nullable(),
  createdAt: isoDateTime,
})
export type Decision = z.infer<typeof Decision>
export const CreateDecision = z.object({
  id: id.optional(),
  title: z.string().trim().min(1).max(500),
  containerId: id.nullable().default(null),
  noteId: id.nullable().optional(),
  status: DecisionStatus.default('accepted'),
  decidedAt: isoDateTime.nullable().optional(),
  rationaleMd: z.string().max(100_000).default(''),
  /** marks the old decision as superseded and links both (`supersedes`) */
  supersedesId: id.nullable().optional(),
})
export const UpdateDecision = patchOf(CreateDecision.omit({ id: true }))
// GET /decisions?containerId=&status=&q= · GET /decisions/:id → Decision & { chain: Decision[] }

// ── Commitments (§19.1 basis) ─────────────────────────────────────────────────────────────────
export const Commitment = z.object({
  id,
  containerId: id.nullable(),
  containerTitle: z.string().nullable(),
  direction: z.enum(['i_owe', 'owed_to_me']),
  personId: id.nullable(),
  personName: z.string().nullable(),
  text: z.string(),
  dueDate: isoDate.nullable(),
  status: z.enum(['open', 'done', 'dropped']),
  taskId: id.nullable(),
  sourceType: z.string().nullable(),
  sourceRef: z.string().nullable(),
  sourceAnchor: z.string().nullable(),
  createdAt: isoDateTime,
})
export type Commitment = z.infer<typeof Commitment>
export const CreateCommitment = z.object({
  id: id.optional(),
  direction: z.enum(['i_owe', 'owed_to_me']),
  text: z.string().trim().min(1).max(2000),
  containerId: id.nullable().default(null),
  personId: id.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
  taskId: id.nullable().optional(),
})
export const UpdateCommitment = patchOf(CreateCommitment.omit({ id: true })).extend({
  status: z.enum(['open', 'done', 'dropped']).optional(),
})
// GET /commitments?direction=&status=&personId=&containerId=

// ── Inbox suggestions (§14.3) ─────────────────────────────────────────────────────────────────
export const SuggestionKind = z.enum([
  'task',
  'note',
  'commitment',
  'decision',
  'fact',
  'classification',
  'agent_result',
])
export const Suggestion = z.object({
  id,
  kind: SuggestionKind,
  status: z.enum(['pending', 'accepted', 'edited', 'rejected']),
  /** kind-specific fields, e.g. task: { title, due, owner_name, mine }, classification: { containerId } */
  payload: z.record(z.string(), z.unknown()),
  confidence: z.number().nullable(),
  rationale: z.string().nullable(),
  containerId: id.nullable(),
  containerTitle: z.string().nullable(),
  meeting: z.object({ id, title: z.string(), startedAt: isoDateTime }).nullable(),
  anchorMs: z.number().nullable(),
  autoAccepted: z.boolean(),
  createdAt: isoDateTime,
})
export type Suggestion = z.infer<typeof Suggestion>
// GET /inbox/suggestions?meetingId= → Suggestion[]
// POST /inbox/triage now also accepts per-item `patch` (edit before accepting):
export const TriageV2 = z.object({
  items: z
    .array(
      z.object({
        kind: z.enum(['note', 'task', 'suggestion']),
        id,
        /** suggestion edits (title, due, containerId, personId, direction, text …) → status 'edited' */
        patch: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .min(1)
    .max(200),
  action: z.enum(['move', 'to_task', 'discard', 'accept']),
  containerId: id.nullable().optional(),
})
export type TriageV2 = z.infer<typeof TriageV2>
// POST /inbox/meetings/:meetingId/accept-all → accepts all pending suggestions of a meeting

// ── Integrations & ingest (§14.1) ─────────────────────────────────────────────────────────────
export const IntegrationClient = z.object({
  id,
  name: z.string(),
  kind: z.enum(['ingest', 'webhook']),
  tokenPrefix: z.string(),
  scopes: z.array(z.string()),
  webhookUrl: z.string().nullable(),
  lastUsedAt: isoDateTime.nullable(),
  revokedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export const CreateIntegrationClient = z.object({
  name: z.string().trim().min(1).max(100),
  scopes: z
    .array(z.enum(['ingest:meetings']))
    .min(1)
    .default(['ingest:meetings']),
  webhookUrl: z.url().nullable().optional(),
})
/** the token is returned exactly once */
export const CreatedIntegrationClient = z.object({ client: IntegrationClient, token: z.string() })

/** POST /api/v1/ingest/meetings – Authorization: Bearer <integration token>, Idempotency-Key required */
export const IngestMeeting = z.object({
  external_id: z.string().trim().min(1).max(300),
  revision: z.number().int().min(1).default(1),
  title: z.string().trim().min(1).max(500),
  started_at: isoDateTime,
  ended_at: isoDateTime.nullable().optional(),
  calendar_event_id: z.string().max(500).nullable().optional(),
  language: z.string().max(10).default('de'),
  participants: z
    .array(z.object({ name: z.string().max(200), email: z.email().nullable().optional() }))
    .max(200)
    .default([]),
  transcript: z.object({ segments: z.array(TranscriptSegment).max(20_000) }).optional(),
  summary_md: z.string().max(200_000).nullable().optional(),
  extracted: z
    .object({
      tasks: z
        .array(
          z.object({
            title: z.string(),
            owner_email: z.string().nullable().optional(),
            due: z.string().nullable().optional(),
            anchor_ms: z.number().nullable().optional(),
          }),
        )
        .default([]),
      decisions: z
        .array(z.object({ title: z.string(), anchor_ms: z.number().nullable().optional() }))
        .default([]),
      commitments: z
        .array(
          z.object({
            direction: z.enum(['i_owe', 'owed_to_me']),
            person_email: z.string().nullable().optional(),
            text: z.string(),
            due: z.string().nullable().optional(),
            anchor_ms: z.number().nullable().optional(),
          }),
        )
        .default([]),
      open_questions: z.array(z.string()).default([]),
    })
    .optional(),
  container_hint: z.string().max(200).nullable().optional(),
})
export type IngestMeeting = z.infer<typeof IngestMeeting>
export const IngestAccepted = z.object({
  meeting_id: id,
  status: z.enum(['processing', 'processed', 'failed']),
  status_url: z.string(),
})

// ── Settings, AI usage, notifications ─────────────────────────────────────────────────────────
export const PriorityWeights = z.object({
  prio: z.number(),
  due: z.number(),
  proj: z.number(),
  commit: z.number(),
  age: z.number(),
  wait: z.number(),
})
export const AutoAcceptRule = z.object({
  kind: z.enum(['task', 'decision', 'commitment']),
  origin: z.literal('meeting'),
  minConfidence: z.number().min(0).max(1),
  containerId: id.nullable(),
})
export const UserSettings = z.object({
  priorityWeights: PriorityWeights,
  reminderTime: z.string().regex(/^\d{2}:\d{2}$/),
  aiMonthlyBudgetEur: z.number().min(0).max(100_000),
  autoAccept: z.array(AutoAcceptRule),
  /** live co-pilot (ADR 0006) */
  copilot: CopilotSettings,
})
export type UserSettings = z.infer<typeof UserSettings>
export const UpdateUserSettings = patchOf(UserSettings.extend({ copilot: patchOf(CopilotSettings) }))
export const AiUsage = z.object({
  month: z.string(),
  spentEur: z.number(),
  budgetEur: z.number(),
  byPurpose: z.array(z.object({ purpose: z.string(), calls: z.number().int(), costEur: z.number() })),
  /** containers whose policy blocks external AI, for the privacy page */
  configured: z.boolean(),
})
export const Notification = z.object({
  id,
  kind: z.string(),
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  readAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
// GET /notifications?unread=true · POST /notifications/read { ids?: uuid[] } (all when omitted)

export const ChangeEventV2Tables = z.enum([
  'containers',
  'container_members',
  'notes',
  'tasks',
  'inbox_items',
  'links',
  'people',
  'meetings',
  'decisions',
  'commitments',
  'notifications',
])
